// An inventory row → the filter values that narrow one list to that row's material and facility.

// The server's `FacilityType`; an inventory row carries exactly one facility id.
export const FACILITY_TYPES = Object.freeze({
  site: 'SITE',
  storage: 'STORAGE',
  backToBack: 'BACK_TO_BACK',
});

// Which filter key each facility type uses; a list without that key cannot be narrowed to it.
const FACILITY_FILTER_KEYS = Object.freeze({
  [FACILITY_TYPES.site]: 'siteId',
  [FACILITY_TYPES.storage]: 'storageId',
  [FACILITY_TYPES.backToBack]: 'backToBackAccountId',
});

// Every id filter key is `[Int!]` server-side, so a stringified id is rejected outright.
const ids = (value) => {
  if (value == null || value === '') return undefined;
  const id = Number(value);
  return Number.isFinite(id) ? [id] : undefined;
};

// `TransactionFilters.terminal` is the one `[String!]` key — it matches by name.
const names = (value) => (value == null || value === '' ? undefined : [String(value)]);

/**
 * The filters for one list, or null when its filter input has no key for this row's facility —
 * querying without them would show another facility's rows as this material's, so the caller
 * renders "unavailable here" instead of sending the query.
 */
export const buildInventoryFilters = (
  { facilityType, facilityId, materialId, terminalId },
  accepts,
) => {
  const facilityKey = FACILITY_FILTER_KEYS[facilityType];
  if (!facilityKey || !accepts.includes(facilityKey)) return null;

  const filters = { materialId: ids(materialId), [facilityKey]: ids(facilityId) };
  if (accepts.includes('terminalRefId')) filters.terminalRefId = ids(terminalId);
  return filters;
};

// The filter keys each list's own input accepts, per the Inventory GraphQL guide.
export const CONTRACT_FILTER_KEYS = [
  'materialId',
  'siteId',
  'backToBackAccountId',
  'terminalRefId',
];
export const SHIPMENT_FILTER_KEYS = [
  'materialId',
  'siteId',
  'backToBackAccountId',
  'terminalRefId',
];
export const DECLARATION_FILTER_KEYS = ['materialId', 'siteId', 'terminalRefId'];
export const PENDING_ORDER_FILTER_KEYS = [
  'materialId',
  'siteId',
  'storageId',
  'backToBackAccountId',
  'terminalRefId',
];
export const OUTGOING_SHIPMENT_FILTER_KEYS = ['materialId', 'siteId', 'terminalRefId'];
export const FULFILLMENT_FILTER_KEYS = ['materialId', 'siteId', 'terminalRefId'];

/** `TransactionFilters` matches the terminal by NAME, not id, and has no storage or B2B key. */
export const buildTransactionFilters = ({ facilityType, facilityId, materialId, terminal }) => {
  if (facilityType !== FACILITY_TYPES.site) return null;
  return { materialId: ids(materialId), siteId: ids(facilityId), terminal: names(terminal) };
};
